import { Link } from "react-router-dom";
import {
  Truck,
  ShieldCheck,
  Package,
  CircleCheck,
  Calendar,
  Layers,
  Sparkles,
  Boxes,
  Monitor,
  Scissors,
  ArrowRight,
} from "lucide-react";
import BigDemenageurCTA from "@/components/BigDemenageurCTA";

const Sections = ({ city }) => (
  <>
    {/* ======= SECTION 2 — Livraison à domicile (3 engagements) ======= */}
    <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Livraison à domicile
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Votre livraison de cartons à domicile {city.aCity}
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Parce que charger des paquets de cartons lourds et encombrants
            ne doit pas gâcher vos préparatifs, notre équipe s&apos;occupe
            de tout acheminer chez vous.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
          {[
            {
              title: "Dépose sur-mesure",
              body: "Nous livrons vos fournitures directement sur le pas de votre porte à Saint-Cyr-l'École, mais aussi à Fontenay-le-Fleury, Guyancourt, Versailles, Bois-d'Arcy et Montigny-le-Bretonneux.",
              Icon: Truck,
            },
            {
              title: "Ponctualité militaire",
              body: "Nous fixons ensemble un créneau horaire précis et strictement respecté pour s'intégrer parfaitement dans votre planning, sans aucune perte de temps.",
              Icon: Calendar,
            },
            {
              title: "Conditionnement pro",
              body: "Vos cartons vous sont remis entièrement à plat, parfaitement propres, secs et protégés sous film étanche pour garantir leur rigidité d'origine.",
              Icon: ShieldCheck,
            },
          ].map(({ title, body, Icon }) => (
            <div
              key={title}
              className="bg-white rounded-2xl border border-[#cfdbe6] p-5 md:p-6"
            >
              <div className="flex items-center gap-2.5">
                <span className="inline-flex items-center justify-center h-10 w-10 rounded-full bg-[#237cb5] text-white">
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <h3 className="font-display font-extrabold text-[15px] text-[#0f1d2b] leading-tight">
                  {title}
                </h3>
              </div>
              <p className="mt-3 text-[14px] text-[#3a4856] leading-relaxed">
                {body}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* ======= SECTION 3 — Kits Prêts à l'emploi ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-7">
            <div className="overline" style={{ color: "#237cb5" }}>
              Packs tout-en-un
            </div>
            <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
              Les kits « Prêts à l&apos;emploi » : gagnez un temps précieux
            </h2>
            <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
              Pour les actifs {city.aCity} qui veulent aller droit au but
              sans risquer d&apos;oublier l&apos;accessoire indispensable le
              jour J, nos experts ont conçu des packs tout-en-un.
            </p>

            <div className="mt-6 space-y-4">
              {[
                {
                  title: "Le concept clé en main",
                  body: "Plus besoin de mesurer ou de deviner vos besoins. Nos kits regroupent la juste quantité de cartons standards, de cartons renforcés, de rouleaux d'adhésif et de film à bulles, proportionnellement à la surface de votre logement.",
                  Icon: Boxes,
                },
                {
                  title: "Des solutions pour tous les volumes",
                  body: "Du pack studio compact idéal pour les jeunes professionnels au grand kit maison pour les projets familiaux, vous trouverez sur notre boutique en ligne la configuration exacte qui correspond à votre déménagement.",
                  Icon: Layers,
                },
              ].map(({ title, body, Icon }) => (
                <div
                  key={title}
                  className="flex items-start gap-3 bg-[#EEF3F7] border border-[#cfdbe6] rounded-2xl p-4 md:p-5"
                >
                  <span className="flex items-center justify-center shrink-0 h-10 w-10 rounded-full bg-[#237cb5] text-white">
                    <Icon className="h-5 w-5" strokeWidth={2.4} />
                  </span>
                  <div>
                    <h3 className="font-display font-extrabold text-[15px] text-[#0f1d2b] leading-tight">
                      {title}
                    </h3>
                    <p className="mt-1.5 text-[14px] text-[#3a4856] leading-relaxed">
                      {body}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            <Link
              to="/packs-cartons"
              className="mt-7 inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
              data-testid="city-saint-cyr-cta-packs"
            >
              <Boxes className="h-4 w-4" strokeWidth={2.6} />
              Voir tous les kits déménagement
              <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
            </Link>
          </div>

          <div className="lg:col-span-5 bg-[#EEF3F7] border border-[#cfdbe6] rounded-3xl p-6">
            <div className="flex items-center gap-3">
              <span className="flex items-center justify-center h-12 w-12 rounded-full bg-[#237cb5] text-white">
                <Boxes className="h-6 w-6" strokeWidth={2.4} />
              </span>
              <div className="font-display font-extrabold text-[20px] text-[#0f1d2b] leading-tight">
                Kits clé en main
              </div>
            </div>
            <p className="mt-4 text-[14px] text-[#3a4856] leading-relaxed">
              Cartons, adhésifs, film à bulles : tout l&apos;indispensable
              dans un seul pack proportionné à la surface de votre logement
              {" "}{city.aCity}.
            </p>
            <div className="mt-5 space-y-2.5 text-[13px] text-[#3a4856]">
              <div className="flex items-center gap-2">
                <CircleCheck className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Pack studio compact
              </div>
              <div className="flex items-center gap-2">
                <CircleCheck className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Pack T2 / T3 famille
              </div>
              <div className="flex items-center gap-2">
                <CircleCheck className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Grand kit maison
              </div>
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Aucun oubli le jour J
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* ======= CTA Big Déménagement (au milieu) ======= */}
    <section className="py-12 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <BigDemenageurCTA testId={`city-${city.slug}-bigdem-cta-middle`} />
      </div>
    </section>

    {/* ======= SECTION 4 — Écrans, High-Tech & Électroménager ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            High-Tech & électroménager
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Écrans, high-tech et électroménager : le blindage de vos
            équipements
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            {city.name} abrite de nombreux foyers connectés. Transporter du
            matériel informatique, des écrans plats ou de l&apos;électroménager
            demande des accessoires de protection spécifiques.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
          {[
            {
              title: "Housses de protection épaisses",
              body: "Ne laissez pas vos matelas, canapés ou gros électroménagers (lave-linge, réfrigérateur) exposés aux frottements dans le camion. Nos housses en polyéthylène haute densité font écran contre la poussière et les rayures.",
              Icon: ShieldCheck,
            },
            {
              title: "Film à bulles & adhésifs techniques",
              body: "Protégez vos écrans de télévision et ordinateurs en les enveloppant de plusieurs couches de film à bulles d'air. Scellez le tout avec notre ruban adhésif à forte adhérence qui se découpe facilement à l'aide de notre dévidoir pistolet pour un blocage immédiat et sans traces.",
              Icon: Scissors,
            },
          ].map(({ title, body, Icon }) => (
            <div
              key={title}
              className="flex items-start gap-4 bg-[#EEF3F7] border border-[#cfdbe6] rounded-2xl p-5 md:p-6"
            >
              <span className="flex items-center justify-center shrink-0 h-11 w-11 rounded-full bg-[#237cb5] text-white">
                <Icon className="h-5 w-5" strokeWidth={2.4} />
              </span>
              <div>
                <h3 className="font-display font-extrabold text-[15.5px] text-[#0f1d2b] leading-tight">
                  {title}
                </h3>
                <p className="mt-2 text-[14px] text-[#3a4856] leading-relaxed">
                  {body}
                </p>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-8 flex flex-wrap gap-3">
          <Link
            to="/emballage-protection"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
            data-testid="city-saint-cyr-cta-emballage"
          >
            <Monitor className="h-4 w-4" strokeWidth={2.6} />
            Voir les protections High-Tech
            <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
          </Link>
        </div>
      </div>
    </section>

    {/* ======= SECTION 5 — CTA retour département ======= */}
    <section className="py-10 md:py-14 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <Link
          to={`/cartons-demenagement/${city.deptSlug}`}
          className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
          data-testid="city-saint-cyr-cta-back-dept"
        >
          <Package className="h-4 w-4" strokeWidth={2.6} />
          Voir la page {city.deptName} ({city.deptCode})
        </Link>
      </div>
    </section>
  </>
);

export default { Sections };
